import { useEffect, useState } from 'react'
import { Button } from 'components'
import { Checkbox, Dropdown, FormInput, FormOverlay, SelectScroll, StyledOption } from 'components/forms'
import { apiFetch } from 'actions/apiFetch'
import { Section, Row, TypeaheadWrap, TypeaheadInput, FieldLabel, FieldWrap } from './components/form-styles'

const RECURRENCE_OPTIONS = [
  ['Does not repeat', 'NONE'],
  ['Daily', 'DAILY'],
  ['Weekly', 'WEEKLY'],
  ['Monthly', 'MONTHLY'],
  ['Yearly', 'YEARLY']
]

const TYPE_OPTIONS = [
  ['Calendar', 'calendar'],
  ['Holiday', 'holiday']
]

const toDatetimeLocal = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const toDateLocal = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

export const EventForm = ({ initial = {}, onSubmit, submitLabel = 'Save', searchPlaces, places }) => {
  const [form, setForm] = useState({
    name: initial.name || '',
    description: initial.description || '',
    event_type: initial.event_type || 'calendar',
    all_day: initial.all_day || false,
    location: initial.location || '',
    recurrance_rule: initial.recurrance_rule || 'NONE',
    start_time: initial.start_time || '',
    end_time: initial.end_time || '',
    address_id: initial.address_id || '',
    queue_id: initial.queue_id || ''
  })

  // Typeahead state (used when places prop is not provided)
  const [locationQuery, setLocationQuery] = useState(
    initial.address?.Location?.name || initial.address?.name || ''
  )
  const [locationSuggestions, setLocationSuggestions] = useState([])
  const [availableQueues, setAvailableQueues] = useState([])

  // Dropdown mode (places prop provided): load queues from the pre-loaded places list
  useEffect(() => {
    if (!places || !form.address_id) return
    const loc = places.find(p => p.addresses?.some(a => a.id === form.address_id))
    const address = loc?.addresses?.find(a => a.id === form.address_id) || loc?.addresses?.[0]
    setAvailableQueues(address?.Queues || [])
  }, [places, form.address_id])

  // Typeahead mode: on edit, load queues for the existing address via its location
  useEffect(() => {
    if (places) return
    const slug = initial.address?.Location?.slug
    if (!slug) return
    apiFetch({ path: `/locations/${slug}` }).then((res) => {
      const loc = res.location
      const address = loc?.addresses?.find(a => a.id === initial.address_id) || loc?.addresses?.[0]
      setAvailableQueues(address?.Queues || [])
    })
  }, [])

  // Debounced location search (typeahead mode only)
  useEffect(() => {
    if (places) return
    if (locationQuery.length < 2) {
      setLocationSuggestions([])
      return
    }
    const t = setTimeout(async () => {
      const res = await searchPlaces(locationQuery)
      setLocationSuggestions(res.locations?.rows || [])
    }, 400)
    return () => clearTimeout(t)
  }, [locationQuery])

  const selectLocation = (location) => {
    const address = location.addresses?.[0]
    setLocationQuery(location.name)
    setLocationSuggestions([])
    setForm(prev => ({ ...prev, address_id: address?.id || '', queue_id: '' }))
    setAvailableQueues(address?.Queues || [])
  }

  const clearLocation = () => {
    setLocationQuery('')
    setLocationSuggestions([])
    setForm(prev => ({ ...prev, address_id: '', queue_id: '' }))
    setAvailableQueues([])
  }

  const handlePlaceDropdown = ({ target: { value } }) => {
    const loc = places.find(p => p.addresses?.some(a => a.id === value))
    const address = loc?.addresses?.find(a => a.id === value) || loc?.addresses?.[0]
    setAvailableQueues(address?.Queues || [])
    setForm(prev => ({ ...prev, address_id: value, queue_id: '' }))
  }

  const handle = ({ target: { name, value } }) => {
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const handleAllDay = ({ target: { checked } }) => {
    setForm(prev => ({
      ...prev,
      all_day: checked,
      start_time: checked ? toDateLocal(prev.start_time) : prev.start_time,
      end_time: checked ? '' : prev.end_time
    }))
  }

  const handleDropdown = (name) => ({ target: { value } }) => {
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = () => {
    const payload = { ...form }
    if (payload.all_day) {
      payload.start_time = payload.start_time ? new Date(payload.start_time).toISOString() : null
      delete payload.end_time
    } else {
      payload.start_time = payload.start_time ? new Date(payload.start_time).toISOString() : null
      payload.end_time = payload.end_time ? new Date(payload.end_time).toISOString() : null
    }
    if (!payload.address_id) delete payload.address_id
    if (!payload.queue_id) delete payload.queue_id
    onSubmit(payload)
  }

  const startValue = form.all_day ? toDateLocal(form.start_time) : toDatetimeLocal(form.start_time)
  const endValue = toDatetimeLocal(form.end_time)

  const queueOptions = [
    ['No queue', ''],
    ...availableQueues.map(q => [q.name, q.id])
  ]

  return (
    <Section>
      <FormInput
        label="Name"
        name="name"
        value={form.name}
        onChange={handle}
      />

      <FormInput
        label="Description"
        name="description"
        value={form.description}
        onChange={handle}
      />

      <Row>
        <div>
          <label>Type</label>
          <Dropdown
            name="event_type"
            data={TYPE_OPTIONS}
            value={form.event_type}
            onChange={handleDropdown('event_type')}
          />
        </div>
        <div>
          <label>Recurrence</label>
          <Dropdown
            name="recurrance_rule"
            data={RECURRENCE_OPTIONS}
            value={form.recurrance_rule}
            onChange={handleDropdown('recurrance_rule')}
          />
        </div>
      </Row>

      <Checkbox
        name="all_day"
        label="All day"
        checked={form.all_day}
        onChange={handleAllDay}
      />

      <FormInput
        label={form.all_day ? 'Date' : 'Start'}
        name="start_time"
        type={form.all_day ? 'date' : 'datetime-local'}
        value={startValue}
        onChange={handle}
      />
      {!form.all_day && (
        <FormInput
          label="End"
          name="end_time"
          type="datetime-local"
          value={endValue}
          onChange={handle}
        />
      )}

      <FormInput
        label="Location (text)"
        name="location"
        value={form.location}
        onChange={handle}
      />

      <FieldWrap>
        <FieldLabel>Place</FieldLabel>
        {places ? (
          <Dropdown
            name="address_id"
            data={[['No place', ''], ...places.map(p => [p.name, p.addresses?.[0]?.id || ''])]}
            value={form.address_id}
            onChange={handlePlaceDropdown}
          />
        ) : (
          <>
            <FormOverlay
              $show={locationSuggestions.length > 0}
              onClick={() => setLocationSuggestions([])}
            />
            <TypeaheadWrap $show={locationSuggestions.length > 0}>
              <TypeaheadInput
                value={locationQuery}
                placeholder="Search for a place..."
                onChange={e => setLocationQuery(e.target.value)}
                onFocus={() => {
                  if (locationQuery.length >= 2) {
                    searchPlaces(locationQuery).then(res =>
                      setLocationSuggestions(res.locations?.rows || [])
                    )
                  }
                }}
              />
              {locationSuggestions.length > 0 && (
                <SelectScroll>
                  {locationSuggestions.map(loc => (
                    <StyledOption key={loc.id} onClick={() => selectLocation(loc)}>
                      {loc.name}
                    </StyledOption>
                  ))}
                </SelectScroll>
              )}
            </TypeaheadWrap>
            {form.address_id && (
              <button type="button" onClick={clearLocation} style={{ marginTop: '0.25rem', fontSize: '0.8rem', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                clear
              </button>
            )}
          </>
        )}
      </FieldWrap>

      {form.address_id && (
        <FieldWrap>
          <FieldLabel>Queue</FieldLabel>
          {availableQueues.length > 0 ? (
            <Dropdown
              name="queue_id"
              data={queueOptions}
              value={form.queue_id || ''}
              onChange={handleDropdown('queue_id')}
            />
          ) : (
            <span style={{ fontSize: '0.85rem', opacity: 0.6 }}>No queues for this place</span>
          )}
        </FieldWrap>
      )}

      <Button onPress={handleSubmit}>{submitLabel}</Button>
    </Section>
  )
}
